/*
  RAVU · página de produto (comportamento do protótipo aprovado, v0.1)
  - cards de 1, 2 e 3 potes com a barra de desconto
  - folha "2. Escolha os sabores": mistura sabores entre os potes
  - adiciona os potes ao carrinho; o desconto de 2 e 3 potes entra pelos descontos automáticos da loja
*/
(() => {
  const brl = (centavos) =>
    (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  /* ---------- selos da foto, lidos da tabela nutricional (mesma regra do protótipo) ---------- */
  function lerFicha(f) {
    if (!f || !f.itens) return null;
    const linhas = f.itens
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => ({ destaque: l.includes('★'), celulas: l.replace('★', '').trim().split('|').map((c) => c.trim()) }));
    return { ...f, cabecalho: f.colunas ? f.colunas.split('|').map((c) => c.trim()) : null, linhas };
  }

  function valorPorcao(t, l) {
    const c = l.celulas;
    return t.cabecalho && /%VD/i.test(t.cabecalho[t.cabecalho.length - 1]) ? c[c.length - 2] : c[c.length - 1];
  }

  function formata(valor, nome) {
    const unidadeNoNome = (nome.match(/\(([^)]+)\)/) || [])[1];
    let v = valor + (unidadeNoNome && /^[\d.,]+$/.test(valor) ? unidadeNoNome : '');
    const m = v.match(/^([\d.,]+)\s*(mg|g|µg|kcal)/i);
    if (m) {
      let n = parseFloat(m[1].replace(/\./g, '').replace(',', '.'));
      let u = m[2];
      if (u === 'mg' && n >= 1000) {
        n = n / 1000;
        u = 'g';
      }
      v = `${String(n).replace('.', ',')} ${u}`;
    }
    return v;
  }

  function selos(ficha) {
    const t = lerFicha(ficha);
    if (!t) return [];
    const out = [];
    const unid = (t.porcao || '').match(/\((\d+)\s*(unidades?|gomas?|pastilhas?)/i) || [];
    if (unid[1]) out.push([`${unid[1]} ${+unid[1] > 1 ? 'gomas' : 'goma'}`, 'por porção']);
    t.linhas
      .filter((l) => l.destaque)
      .forEach((l) => {
        const nome = l.celulas[0].replace(/\s*\(.*\)\s*/, '');
        out.push([formata(valorPorcao(t, l), l.celulas[0]), `de ${nome.toLowerCase()}`]);
      });
    const acucar = t.linhas.find((l) => /^Açúcares adicionados/i.test(l.celulas[0]));
    if (acucar && parseFloat(valorPorcao(t, acucar)) === 0) out.push(['0 g', 'de açúcar adicionado']);
    return out.slice(0, 4);
  }

  /* ---------- página ---------- */
  class RavuProduto {
    constructor(raiz) {
      this.raiz = raiz;
      this.$ = (s) => raiz.querySelector(s);
      this.$$ = (s) => [...raiz.querySelectorAll(s)];
      this.dados = JSON.parse(this.$('[data-ravu-dados]').textContent);
      /* produto em promoção própria (fora do progressivo, ex.: Pump a 20%): começa em 1 pote */
      this.potes = this.dados.semProg ? 1 : 3;
      this.mix = {};
      this.linhas = this.$$('[data-variante]');
      this.fichaAtual = null;

      this.iniciarCarrossel();
      this.iniciarQuantidade();
      this.iniciarSabores();
      this.iniciarAbas();
      this.iniciarBarraFixa();
      this.resetarMix();
      this.atualizar();
      document.addEventListener('ravu:carrinho', () => this.atualizar());
      setTimeout(() => this.atualizar(), 1500);
    }

    /* carrossel de fotos */
    iniciarCarrossel() {
      const janela = this.$('[data-window]');
      if (!janela) return;
      const total = janela.children.length;
      const indice = () => Math.round(janela.scrollLeft / janela.clientWidth);
      const ir = (i) =>
        janela.scrollTo({ left: janela.clientWidth * Math.max(0, Math.min(total - 1, i)), behavior: 'smooth' });
      this.$('[data-prev]')?.addEventListener('click', () => ir(indice() - 1));
      this.$('[data-next]')?.addEventListener('click', () => ir(indice() + 1));
      this.$('[data-thumbs]')?.addEventListener('click', (e) => {
        const b = e.target.closest('[data-i]');
        if (b) ir(+b.dataset.i);
      });
      janela.addEventListener(
        'scroll',
        () => {
          const i = indice();
          this.$$('[data-dots] i').forEach((d, k) => d.classList.toggle('on', k === i));
          this.$$('[data-thumbs] button').forEach((b, k) => b.classList.toggle('on', k === i));
          const prev = this.$('[data-prev]');
          const next = this.$('[data-next]');
          if (prev) prev.hidden = i === 0;
          if (next) next.hidden = i >= total - 1;
          const pills = this.$('[data-pills]');
          if (pills) pills.style.opacity = i === 0 ? 1 : 0;
        },
        { passive: true }
      );
    }

    /* quantidade: 1, 2 ou 3 potes */
    iniciarQuantidade() {
      const definir = (n) => {
        const saborAtual = this.dados.pacotes ? this.sabor() : null;
        this.potes = n;
        /* modo pacote: mantém o sabor já escolhido ao trocar a quantidade */
        if (saborAtual && this.dados.pacotes[saborAtual]) this.mix = { [saborAtual]: this.potes };
        else this.resetarMix();
        this.atualizar();
      };
      const escolher = (e) => {
        const b = e.target.closest('[data-n]');
        if (b) definir(+b.dataset.n);
      };
      /* desconto progressivo: a partir de 3, "−" e "+" escolhem quantos potes (até 12), todos com o desconto de 3 */
      this.$('[data-qmais-menos]')?.addEventListener('click', () => { if (this.potes > 3) definir(this.potes - 1); });
      this.$('[data-qmais-mais]')?.addEventListener('click', () => definir(Math.min(12, Math.max(3, this.potes + 1))));
      this.$('[data-qtys]')?.addEventListener('click', escolher);
      this.$('[data-progress]')?.addEventListener('click', escolher);
      const principal = () => (this.dados.temSabores ? this.abrirFolha() : this.adicionar());
      this.$('[data-cta]')?.addEventListener('click', principal);
      this.$('[data-sticky-add]')?.addEventListener('click', principal);
    }

    parcela(total) {
      const p = window.RAVU_CONFIG?.parcelas || 3;
      return `Ou ${p}x de ${brl(Math.round(total / p))} sem juros`;
    }

    /* desconto progressivo (Yampi · Leve mais, pague menos): 2 potes = desconto de 2; 3 ou mais = desconto de 3.
       Os potes vão ao carrinho 1 a 1 (variação de 1 unidade); o preço é o de 1 pote × quantidade − o desconto. */
    pctProg(n) {
      if (this.dados.semProg) return 0;
      const d = this.dados.descontoProg || {};
      return n >= 3 ? +d[3] || 0 : n === 2 ? +d[2] || 0 : 0;
    }

    /* potes avulsos que já estão no carrinho (combos fora): entram na conta do desconto dos potes desta página */
    potesNoCarrinho() {
      const itens = (window.RAVU && window.RAVU.carrinho && window.RAVU.carrinho.dados && window.RAVU.carrinho.dados.items) || [];
      const cat = (window.RAVU && window.RAVU.catalogo) || [];
      const tipo = (h) => (cat.find((p) => p.handle === h) || {}).tipo;
      const semProg = (h) => (cat.find((p) => p.handle === h) || {}).semProg;
      return itens.filter((i) => tipo(i.handle) && tipo(i.handle) !== 'combo' && !semProg(i.handle)).reduce((t, i) => t + i.quantity, 0);
    }

    pctEfetivo(n) {
      return this.pctProg(n + this.potesNoCarrinho());
    }

    preco1(id) {
      const p = this.dados.pacotes && this.dados.pacotes[id];
      return (p && p[1] && p[1].preco) || this.dados.precoPote;
    }

    baseProg(n) {
      const ids = Object.keys(this.mix).filter((id) => this.mix[id] > 0);
      const usados = ids.reduce((t, id) => t + this.mix[id], 0);
      if (usados === n) return ids.reduce((t, id) => t + this.mix[id] * this.preco1(id), 0);
      return this.preco1(this.sabor()) * n;
    }

    /* modo pacote (loja oficial): sabor escolhido → variações reais de 1, 2 e 3 potes.
       Com a chave "misturar sabores" (teste de 18/10), o cliente mistura e o pacote vai com o sabor que tem mais potes;
       a mistura vai como anotação "Sabores" na linha do carrinho (ex.: "2 Uva Roxa e 1 Limão Siciliano"). */
    umSabor() {
      return !!this.dados.pacotes && !this.dados.misturar && !this.dados.progressivo;
    }

    sabor() {
      const ids = Object.keys(this.mix).filter((id) => this.mix[id] > 0).sort((a, b) => this.mix[b] - this.mix[a]);
      return ids[0] || String(this.dados.varianteUnica);
    }

    anotacaoSabores() {
      if (this.dados.progressivo) return null;
      const ids = Object.keys(this.mix).filter((id) => this.mix[id] > 0);
      if (!this.dados.pacotes || ids.length < 2) return null;
      /* sem "+": no link do checkout da Yampi o "+" vira espaço. Ex.: "2 Uva Roxa e 1 Limão Siciliano" */
      const partes = ids
        .sort((a, b) => this.mix[b] - this.mix[a])
        .map((id) => `${this.mix[id]} ${this.linhas.find((l) => l.dataset.variante === id)?.dataset.nome || ''}`.trim());
      return partes.length > 1 ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}` : partes[0];
    }

    pacote(n = this.potes) {
      const p = this.dados.pacotes;
      if (!p) return null;
      const s = p[this.sabor()] || p[Object.keys(p)[0]];
      return s ? s[n] : null;
    }

    total(n = this.potes) {
      if (this.dados.progressivo) return Math.round((this.baseProg(n) * (100 - this.pctEfetivo(n))) / 100);
      const pac = this.pacote(n);
      if (pac) return pac.preco;
      const off = this.dados.descontos[n] || 0;
      return Math.round((this.dados.precoPote * n * (100 - off)) / 100);
    }

    /* promoção própria: o preço cheio é o "de" da variação de 1 pote (ex.: Pump de R$ 72,00) × potes */
    cheioPromo(n) {
      const p = this.dados.pacotes;
      const s = p && (p[this.sabor()] || p[Object.keys(p)[0]]);
      const c1 = (s && s[1] && Math.max(s[1].cheio || 0, s[1].preco || 0)) || this.dados.precoPote;
      return c1 * n;
    }

    cheio(n = this.potes) {
      if (this.dados.progressivo && this.dados.semProg) return this.cheioPromo(n);
      if (this.dados.progressivo) return this.baseProg(n);
      const pac = this.pacote(n);
      if (pac) return Math.max(pac.cheio, pac.preco);
      return this.dados.precoPote * n;
    }

    desconto(n = this.potes) {
      if (this.dados.progressivo && this.dados.semProg) {
        const c = this.cheio(n);
        return c > 0 ? Math.floor(((c - this.total(n)) * 100) / c) : 0;
      }
      if (this.dados.progressivo) return this.pctEfetivo(n);
      if (!this.dados.pacotes) return this.dados.descontos[n] || 0;
      const c = this.cheio(n);
      return c > 0 ? Math.floor(((c - this.total(n)) * 100) / c) : 0;
    }

    atualizar() {
      const n = this.potes;
      const total = this.total();
      const off = this.desconto(n);
      const cheio = this.cheio(n);

      const n3 = Math.min(n, 3);
      this.$$('.qcard').forEach((c) => {
        const on = +c.dataset.n === n3;
        c.classList.toggle('on', on);
        c.setAttribute('aria-checked', on);
      });
      const fill = this.$('[data-pfill]');
      if (fill) fill.style.width = { 1: '0%', 2: '50%', 3: '100%' }[n3];
      this.$$('[data-progress] .pcol').forEach((c, k) => {
        c.classList.toggle('on', k < n3);
        c.classList.toggle('cur', k === n3 - 1);
      });
      if (this.dados.progressivo) this.cartoesProg(n);

      this.$('[data-summary]').innerHTML = `<div class="chips2"><span>${brl(total / n)}/pote</span>${
        off ? `<span class="off">${off}% OFF</span>` : ''
      }</div><div class="tot">${cheio > total ? `de <s>${brl(cheio)}</s>` : 'total'}<b>${brl(total)}</b></div>`;

      const textoCta = this.$('[data-cta-texto]');
      if (textoCta && !this.$('[data-cta]').disabled) {
        textoCta.textContent = this.dados.temSabores
          ? `2. Selecionar ${n > 1 ? 'sabores' : 'sabor'}`
          : 'Adicionar ao carrinho';
      }
      this.$('[data-payline]').textContent = `${this.parcela(total)} · Pix e boleto`;
      this.dicaCarrinho();
      this.$('[data-sticky-total]').textContent = brl(total);
      this.$('[data-sticky-nome]').textContent = `${n} ${n > 1 ? 'potes' : 'pote'} · ${this.dados.produto}`;

      /* sabor principal mudou: selos e tabela passam para a ficha dele */
      const principal = Object.keys(this.mix).find((id) => this.mix[id] > 0) || this.dados.varianteUnica;
      const linhaPrincipal = this.linhas.find((l) => l.dataset.variante === String(principal));
      const fotoFixa = this.$('[data-sticky] img');
      if (fotoFixa && linhaPrincipal?.dataset.foto) fotoFixa.src = linhaPrincipal.dataset.foto;
      const ficha = this.dados.fichaPorVariante[principal];
      if (ficha && ficha !== this.fichaAtual) {
        this.fichaAtual = ficha;
        this.mostrarSelos(ficha);
        this.mostrarFicha(ficha);
      }
    }

    /* desconto progressivo: os cartões e a barra contam também os potes avulsos que já estão no carrinho
       (ex.: 1 pote no carrinho → o cartão "1 pote" já mostra 10% OFF). O cartão de 3 vira "N potes" a partir de 4;
       "−/+" só aparecem a partir de 3; "Melhor valor" sai quando já há potes no carrinho. */
    cartoesProg(n) {
      const noCarrinho = this.potesNoCarrinho();
      const mais = this.$('[data-qmais]');
      if (mais) {
        mais.hidden = n < 3 || !!this.dados.semProg;
        const num = mais.querySelector('[data-qmais-n]');
        if (num) num.textContent = n;
      }
      this.$$('.qcard').forEach((c) => {
        const k = +c.dataset.n;
        const nk = k === 3 ? Math.max(n, 3) : k;
        const t = this.total(nk);
        const ch = this.cheio(nk);
        const off = this.desconto(nk);
        if (!c.dataset.dias) c.dataset.dias = (c.querySelector('.days') || {}).textContent || '';
        c.querySelector('.nm b').textContent = `${nk} ${nk > 1 ? 'potes' : 'pote'}`;
        const per = c.querySelector('.per');
        if (per) per.textContent = off ? `${brl(t / nk)}/pote` : 'preço normal';
        const pr = c.querySelector('.pr');
        if (pr) pr.innerHTML = `${brl(t)}${ch > t ? `<s>${brl(ch)}</s>` : ''}`;
        const canto = c.querySelector('.corner');
        if (canto) canto.innerHTML = `${off ? `<span class="off">${off}%OFF</span>` : ''}${k === 3 && !noCarrinho && !this.dados.semProg ? '<span class="best">Melhor valor</span>' : ''}`;
        const dias = c.querySelector('.days');
        const m = c.dataset.dias.match(/(\d+)(.*)/);
        if (dias && m) dias.textContent = `${Math.round((+m[1] / k) * nk)}${m[2]}`;
      });
      const barra = this.$('[data-progress]');
      if (barra) {
        barra.hidden = this.pctEfetivo(1) === this.pctEfetivo(3);
        barra.querySelectorAll('.pcol').forEach((c) => {
          const k = +c.dataset.n;
          const off = this.desconto(k);
          c.querySelector('.ptag').textContent = off ? `${off}% OFF` : '1 pote';
          c.querySelector('small').textContent = `${brl(this.total(k) / k)}/pote`;
        });
      }
    }

    /* desconto progressivo: avisa quantos potes avulsos já estão no carrinho e o desconto que o pedido inteiro vai ter */
    dicaCarrinho() {
      const el = this.$('[data-prog-dica]');
      if (!el || !this.dados.progressivo) return;
      if (this.dados.semProg) return void (el.hidden = true);
      const noCarrinho = this.potesNoCarrinho();
      if (!noCarrinho) return void (el.hidden = true);
      const pct = this.pctProg(noCarrinho + this.potes);
      el.hidden = false;
      el.innerHTML = `Você já tem <b>${noCarrinho} ${noCarrinho > 1 ? 'potes' : 'pote'}</b> no carrinho. ${
        pct ? `Com ${this.potes > 1 ? 'estes' : 'este'}, o pedido fica com <b>${pct}% OFF</b> em todos os potes.` : ''
      }`;
    }

    mostrarSelos(handle) {
      const lista = selos(this.dados.fichas[handle]);
      const pills = this.$('[data-pills]');
      if (!pills || !lista.length) return;
      pills.innerHTML = lista.map(([v, l]) => `<span><b>${v}</b> ${l}</span>`).join('');
    }

    /* sabores */
    disponiveis() {
      return this.linhas.filter((l) => l.dataset.disponivel === 'true');
    }

    resetarMix() {
      this.mix = {};
      /* sabor que veio no link (?variant=…, ex.: banner de lançamento) ou o primeiro disponível */
      /* no modo pacote o link pode trazer "Chocolate / 3 Unidades": vale o sabor dele */
      const selecionada = this.dados.saborDaVariante?.[this.dados.varianteSelecionada] || this.dados.varianteSelecionada;
      const pedida = this.disponiveis().find((l) => l.dataset.variante === String(selecionada));
      const primeira = pedida || this.disponiveis()[0];
      if (primeira) this.mix[primeira.dataset.variante] = this.potes;
    }

    usados() {
      return Object.values(this.mix).reduce((a, b) => a + b, 0);
    }

    iniciarSabores() {
      this.folha = this.$('[data-sheet]');
      if (!this.folha) return;
      this.folha.addEventListener('click', (e) => {
        if (e.target.closest('[data-sheet-close]')) return this.fecharFolha();
        const linha = e.target.closest('[data-variante]');
        if (!linha || linha.dataset.disponivel !== 'true') return;
        const id = linha.dataset.variante;
        const q = this.mix[id] || 0;
        /* sabor de outro produto (ex.: Framboesa na página da Uva ou Limão): vai em pacote só dele, sem mistura */
        const irmao = linha.dataset.irmao === 'true' && !this.dados.progressivo;
        if (irmao && e.target.closest('[data-menos]')) {
          delete this.mix[id];
        } else if (this.potes === 1 || this.umSabor() || irmao) {
          /* modo pacote: um sabor por compra; todos os potes ficam desse sabor */
          this.mix = { [id]: this.potes };
        } else if (e.target.closest('[data-mais]')) {
          if (!this.dados.progressivo) this.linhas.filter((l) => l.dataset.irmao === 'true').forEach((l) => delete this.mix[l.dataset.variante]);
          if (this.usados() < this.potes) this.mix[id] = (this.mix[id] || 0) + 1;
        } else if (e.target.closest('[data-menos]') && q > 0) {
          this.mix[id] = q - 1;
        }
        this.desenharFolha();
        this.atualizar();
      });
      this.folha.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.fecharFolha();
      });
      this.$('[data-sheet-add]').addEventListener('click', () => this.adicionar());
    }

    abrirFolha() {
      this.desenharFolha();
      this.folha.classList.add('open');
      this.folha.setAttribute('aria-hidden', 'false');
      this.ultimoFoco = document.activeElement;
      this.$('[data-sheet-add]').focus({ preventScroll: true });
    }

    fecharFolha() {
      if (!this.folha) return;
      this.folha.classList.remove('open');
      this.folha.setAttribute('aria-hidden', 'true');
      this.ultimoFoco?.focus({ preventScroll: true });
    }

    desenharFolha() {
      const um = this.potes === 1 || this.umSabor();
      const usados = this.usados();
      this.folha.dataset.modo = um ? 'um' : 'varios';
      this.$('[data-sheet-titulo]').textContent = um ? '2. Escolha o sabor' : '2. Escolha os sabores';
      this.$('[data-need]').innerHTML = um
        ? this.potes > 1
          ? `Toque no sabor dos seus <b>${this.potes} potes</b>.`
          : 'Toque no sabor do seu pote.'
        : `Escolha <b>${this.potes} potes</b> e misture como quiser · ${usados}/${this.potes}`;
      this.linhas.forEach((l) => {
        const q = this.mix[l.dataset.variante] || 0;
        l.classList.toggle('on', q > 0);
        l.querySelector('[data-qtd]').textContent = q;
        if (um) {
          l.setAttribute('role', 'radio');
          l.setAttribute('aria-checked', q > 0);
          l.tabIndex = 0;
        } else {
          l.removeAttribute('role');
          l.removeAttribute('aria-checked');
          l.removeAttribute('tabindex');
        }
      });
      const ok = usados === this.potes;
      const botao = this.$('[data-sheet-add]');
      botao.disabled = !ok;
      const falta = this.potes - usados;
      botao.innerHTML = ok
        ? '<span>Adicionar ao carrinho</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
        : `<span>Faltam ${falta} ${falta > 1 ? 'potes' : 'pote'}</span>`;
      const total = this.total();
      this.$('[data-sheet-total]').textContent = `${this.potes} ${this.potes > 1 ? 'potes' : 'pote'} · ${brl(
        total
      )} · ${this.parcela(total)}`;
    }

    /* tabela nutricional: abas por sabor */
    iniciarAbas() {
      /* os cliques nas abas ficam com o ravu-app.js; aqui a tabela acompanha o sabor escolhido */
    }

    mostrarFicha(handle) {
      /* todas as tabelas da página (acordeão e "O que tem em…") passam para a ficha do sabor */
      document.querySelectorAll('[data-fichas]').forEach((grupo) => {
        const alvo = grupo.querySelector(`[data-ficha="${handle}"]`);
        if (!alvo) return;
        grupo.querySelectorAll('[data-ficha]').forEach((el) => (el.hidden = el !== alvo));
        grupo.querySelectorAll('[data-aba]').forEach((b) => {
          const on = b.dataset.aba === handle;
          b.classList.toggle('on', on);
          b.setAttribute('aria-selected', on);
        });
      });
    }

    /* carrinho */
    async adicionar() {
      /* modo pacote: 1 item = a variação do sabor com N potes (ex.: "Chocolate / 3 Unidades") */
      /* progressivo: nada de pacote; cada pote vai como variação de 1 unidade (o desconto quem dá é a Yampi, no checkout) */
      const pac = this.dados.progressivo ? null : this.pacote();
      if (pac && !pac.disponivel) {
        const erro = this.$('[data-erro]');
        erro.textContent = 'Esta opção está esgotada no momento. Escolha outra quantidade ou outro sabor.';
        erro.hidden = false;
        this.fecharFolha();
        return;
      }
      const anotacao = this.anotacaoSabores();
      const itens = pac
        ? [anotacao ? { id: pac.id, quantity: 1, properties: { Sabores: anotacao } } : { id: pac.id, quantity: 1 }]
        : this.dados.temSabores
        ? Object.entries(this.mix)
            .filter(([, q]) => q > 0)
            .map(([id, quantity]) => ({ id: +id, quantity }))
        : [{ id: this.dados.varianteUnica, quantity: this.potes }];
      if (!itens.length) return;

      const botoes = this.$$('[data-cta], [data-sheet-add], [data-sticky-add]');
      botoes.forEach((b) => b.classList.add('is-loading'));
      const erro = this.$('[data-erro]');
      erro.hidden = true;

      try {
        if (!window.RAVU?.carrinho) throw new Error('Carrinho indisponível. Recarregue a página.');
        await window.RAVU.carrinho.adicionar(itens);
        this.fecharFolha();
      } catch (e) {
        erro.textContent = e.message;
        erro.hidden = false;
        this.fecharFolha();
      } finally {
        botoes.forEach((b) => b.classList.remove('is-loading'));
      }
    }

    /* barra fixa quando o botão principal sai da tela */
    iniciarBarraFixa() {
      const barra = this.$('[data-sticky]');
      const cta = this.$('[data-cta]');
      if (!barra || !cta) return;
      new IntersectionObserver(([e]) => {
        const mostrar = !e.isIntersecting && e.boundingClientRect.top < 0;
        barra.classList.toggle('show', mostrar);
        barra.setAttribute('aria-hidden', !mostrar);
        document.body.classList.toggle('has-sticky', mostrar);
      }).observe(cta);
    }
  }

  const iniciar = () =>
    document.querySelectorAll('[data-ravu-produto]:not([data-pronto])').forEach((raiz) => {
      try {
        new RavuProduto(raiz);
        raiz.dataset.pronto = '';
      } catch (e) {
        console.error('RAVU · página de produto:', e);
      }
    });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
  document.addEventListener('shopify:section:load', iniciar);
})();
